import { useState, useCallback, useEffect, useMemo } from 'react';
import { companiesApi } from '../../../../services/api/companies';
import { ApiError } from '../../../../services/api/client';
import {
  CompanyWithShareholders,
  ShareholderCreate,
  ShareholderUpdate,
  CompanyUpdate,
} from '../../../../types';
import { hasAnyFieldChanged } from '../../../../utils/formUtils';

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return fallback;
}

interface UseCompanyDataProps {
  projectId: string;
}

interface UseCompanyDataReturn {
  company: CompanyWithShareholders | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  remainingPercent: number;
  totalPercent: number;
  updateCompanyField: (field: string, value: string | null) => Promise<void>;
  createShareholder: (data: ShareholderCreate) => Promise<CompanyWithShareholders>;
  updateShareholder: (shareholderId: string, data: ShareholderUpdate) => Promise<CompanyWithShareholders>;
  deleteShareholder: (shareholderId: string) => Promise<void>;
  refetchCompany: () => Promise<void>;
}

/**
 * Hook für Gesellschafts- und Gesellschafter-Daten.
 * Eigener Daten-Lifecycle, unabhängig von useFrameworkData.
 */
export const useCompanyData = ({ projectId }: UseCompanyDataProps): UseCompanyDataReturn => {
  const [company, setCompany] = useState<CompanyWithShareholders | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompany = useCallback(async (signal?: { cancelled: boolean }) => {
    try {
      const result = await companiesApi.getCompany(projectId);
      if (!signal || !signal.cancelled) setCompany(result);
    } catch (err) {
      if (!signal || !signal.cancelled) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden');
      }
    }
  }, [projectId]);

  useEffect(() => {
    const signal = { cancelled: false };
    setIsLoading(true);
    setError(null);
    fetchCompany(signal).finally(() => {
      if (!signal.cancelled) setIsLoading(false);
    });
    return () => { signal.cancelled = true; };
  }, [fetchCompany]);

  const refetchCompany = useCallback(async () => {
    await fetchCompany();
  }, [fetchCompany]);

  const totalPercent = useMemo(() => {
    if (!company?.shareholders) return 0;
    return company.shareholders.reduce((sum, sh) => sum + sh.anteil_prozent, 0);
  }, [company?.shareholders]);

  const remainingPercent = useMemo(() => {
    return Math.max(0, 100 - totalPercent);
  }, [totalPercent]);

  const updateCompanyField = useCallback(async (field: string, value: string | null) => {
    if (!hasAnyFieldChanged(company, { [field]: value })) return;

    try {
      setIsSaving(true);
      setError(null);
      const updated = await companiesApi.updateCompany(projectId, { [field]: value } as CompanyUpdate);
      setCompany(updated);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Fehler beim Speichern'));
    } finally {
      setIsSaving(false);
    }
  }, [projectId, company]);

  const createShareholder = useCallback(async (data: ShareholderCreate): Promise<CompanyWithShareholders> => {
    try {
      setIsSaving(true);
      setError(null);
      const updated = await companiesApi.createShareholder(projectId, data);
      setCompany(updated);
      return updated;
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Fehler beim Erstellen'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [projectId]);

  const updateShareholder = useCallback(async (
    shareholderId: string,
    data: ShareholderUpdate,
  ): Promise<CompanyWithShareholders> => {
    const currentShareholder = company?.shareholders?.find(sh => sh.id === shareholderId);
    if (currentShareholder && !hasAnyFieldChanged(currentShareholder, data)) {
      return company!;
    }

    try {
      setIsSaving(true);
      setError(null);
      const updated = await companiesApi.updateShareholder(projectId, shareholderId, data);
      setCompany(updated);
      return updated;
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Fehler beim Speichern'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [projectId, company]);

  const deleteShareholder = useCallback(async (shareholderId: string) => {
    try {
      setIsSaving(true);
      setError(null);
      const updated = await companiesApi.deleteShareholder(projectId, shareholderId);
      setCompany(updated);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Fehler beim Löschen'));
    } finally {
      setIsSaving(false);
    }
  }, [projectId]);

  return {
    company,
    isLoading,
    isSaving,
    error,
    remainingPercent,
    totalPercent,
    updateCompanyField,
    createShareholder,
    updateShareholder,
    deleteShareholder,
    refetchCompany,
  };
};
